import React from 'react';
import { Check, X, ShieldCheck, ShieldAlert } from 'lucide-react';

export const checkPasswordCriteria = (password = '') => {
  return [
    {
      id: 'length',
      label: 'Minimum 8 characters',
      valid: password.length >= 8,
    },
    {
      id: 'uppercase',
      label: 'At least 1 uppercase letter (A-Z)',
      valid: /[A-Z]/.test(password),
    },
    {
      id: 'lowercase',
      label: 'At least 1 lowercase letter (a-z)',
      valid: /[a-z]/.test(password),
    },
    {
      id: 'number',
      label: 'At least 1 number (0-9)',
      valid: /[0-9]/.test(password),
    },
    {
      id: 'special',
      label: 'At least 1 special character (@$!%*?& etc.)',
      valid: /[^A-Za-z0-9]/.test(password),
    },
  ];
};

export const getPasswordValidation = (password = '') => {
  const criteria = checkPasswordCriteria(password);
  const metCount = criteria.filter((c) => c.valid).length;
  const isValid = metCount === criteria.length;

  let strengthLabel = 'Very Weak';
  let strengthColor = 'bg-rose-500';
  let badgeBg = 'bg-rose-100 text-rose-700 border-rose-200';

  if (metCount === 5) {
    strengthLabel = 'Strong & Secure';
    strengthColor = 'bg-emerald-500';
    badgeBg = 'bg-emerald-100 text-emerald-700 border-emerald-200';
  } else if (metCount >= 4) {
    strengthLabel = 'Good';
    strengthColor = 'bg-blue-500';
    badgeBg = 'bg-blue-100 text-blue-700 border-blue-200';
  } else if (metCount >= 2) {
    strengthLabel = 'Fair';
    strengthColor = 'bg-amber-500';
    badgeBg = 'bg-amber-100 text-amber-700 border-amber-200';
  }

  let firstError = null;
  if (!isValid) {
    const unmet = criteria.find((c) => !c.valid);
    if (unmet) {
      if (unmet.id === 'length') firstError = 'Password must be at least 8 characters long';
      else if (unmet.id === 'uppercase') firstError = 'Password must contain at least one uppercase letter (A-Z)';
      else if (unmet.id === 'lowercase') firstError = 'Password must contain at least one lowercase letter (a-z)';
      else if (unmet.id === 'number') firstError = 'Password must contain at least one number (0-9)';
      else if (unmet.id === 'special') firstError = 'Password must contain at least one special character (@$!%*?& etc.)';
    }
  }

  return {
    criteria,
    metCount,
    totalCount: criteria.length,
    percentage: (metCount / criteria.length) * 100,
    isValid,
    strengthLabel,
    strengthColor,
    badgeBg,
    firstError,
  };
};

const PasswordStrengthGuide = ({ password = '', showOnlyOnFocus = false, isFocused = true }) => {
  // If showOnlyOnFocus is true, don't show when not focused and no password entered
  if (showOnlyOnFocus && !isFocused && !password) {
    return null;
  }

  const { criteria, metCount, totalCount, isValid, strengthLabel, strengthColor, badgeBg } =
    getPasswordValidation(password);

  return (
    <div className="mt-3 p-4 bg-slate-50 border border-slate-200 rounded-2xl shadow-sm text-left transition-all">
      {/* Header & Strength Bar */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {isValid ? (
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
          ) : (
            <ShieldAlert className="w-4 h-4 text-slate-500" />
          )}
          <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
            Password Policy Guide
          </span>
        </div>
        {password && (
          <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full border ${badgeBg}`}>
            {strengthLabel} ({metCount}/{totalCount})
          </span>
        )}
      </div>

      {/* Progress Bars (5 Segments) */}
      <div className="grid grid-cols-5 gap-1.5 mb-3">
        {Array.from({ length: 5 }).map((_, idx) => (
          <div
            key={idx}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              idx < metCount ? strengthColor : 'bg-slate-200'
            }`}
          />
        ))}
      </div>

      {/* Requirements Checklist */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pt-1">
        {criteria.map((item) => {
          const isPassed = item.valid;
          return (
            <div
              key={item.id}
              className={`flex items-center gap-2 text-xs transition-colors ${
                isPassed
                  ? 'text-emerald-700 font-semibold'
                  : password
                  ? 'text-slate-500'
                  : 'text-slate-400'
              }`}
            >
              <div
                className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 transition-all ${
                  isPassed
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-slate-200 text-slate-400'
                }`}
              >
                {isPassed ? (
                  <Check className="w-3 h-3 stroke-[3]" />
                ) : (
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                )}
              </div>
              <span className="leading-tight">{item.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PasswordStrengthGuide;
